import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface Step {
  id: string;
  label: string;
  description?: string;
  /** If true, show a static disabled indicator (locked) */
  disabled?: boolean;
}

interface StepperProps {
  steps: Step[];
  currentIndex: number;
  /** Click to jump to a step (only allowed for completed/current) */
  onStepClick?: (index: number) => void;
  className?: string;
}

/**
 * Vertical (sidebar) stepper for multi-step wizards.
 * - Completed steps show a check, are clickable.
 * - Current step has indigo accent.
 * - Future steps are gray, not clickable.
 */
export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentIndex,
  onStepClick,
  className,
}) => {
  return (
    <ol className={cn('flex flex-col gap-1', className)}>
      {steps.map((step, idx) => {
        const isCompleted = idx < currentIndex;
        const isCurrent = idx === currentIndex;
        const isFuture = idx > currentIndex;
        const clickable = !step.disabled && (isCompleted || isCurrent) && !!onStepClick;

        return (
          <li key={step.id} className="relative pl-8 pb-3 last:pb-0">
            {/* Connector line */}
            {idx < steps.length - 1 && (
              <span
                className={cn(
                  'absolute left-3 top-8 bottom-0 w-px',
                  isCompleted ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-zinc-800'
                )}
              />
            )}

            {/* Step indicator */}
            <span
              className={cn(
                'absolute left-0 top-0 h-6 w-6 rounded-full flex items-center justify-center text-2xs font-semibold',
                isCompleted && 'bg-indigo-600 text-white',
                isCurrent &&
                  'bg-white border-2 border-indigo-600 text-indigo-600 ring-4 ring-indigo-100 dark:bg-zinc-900 dark:text-indigo-400 dark:ring-indigo-950',
                isFuture && 'bg-white border border-gray-300 text-gray-400 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-500'
              )}
            >
              {isCompleted ? <Check className="h-3 w-3" /> : idx + 1}
            </span>

            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(idx)}
              className={cn(
                'block w-full text-left -mt-0.5',
                clickable ? 'cursor-pointer' : 'cursor-default'
              )}
            >
              <div
                className={cn(
                  'text-xs font-semibold leading-tight',
                  isCurrent && 'text-indigo-700 dark:text-indigo-400',
                  isCompleted && 'text-gray-900 dark:text-zinc-100',
                  isFuture && 'text-gray-400 dark:text-zinc-500'
                )}
              >
                {step.label}
              </div>
              {step.description && (
                <div
                  className={cn(
                    'mt-0.5 text-2xs leading-snug',
                    isFuture ? 'text-gray-400 dark:text-zinc-500' : 'text-gray-500 dark:text-zinc-400'
                  )}
                >
                  {step.description}
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
};

export default Stepper;
